/**
 * Web path -> native route mapping.
 *
 * The native router only registers `/`, `/staff` and `/app/*`, and its
 * catch-all silently renders the dashboard. Push payloads and deep links are
 * written against the WEB paths (`/staff/tasks`, `/staff/messages`, ...), so on
 * a phone every one of them landed on the dashboard with no indication that
 * the intended destination was dropped.
 *
 * Also the choke point for externally supplied navigation targets: push
 * payloads come off the network, so only same-origin relative paths are
 * allowed through — never an absolute URL to somewhere else.
 */

const STAFF_TO_APP: Record<string, string> = {
  "": "/app",
  roster: "/app/roster",
  "my-roster": "/app/my-roster",
  schedule: "/app/roster",
  messages: "/app/messages",
  tasks: "/app/tasks",
  profile: "/app/profile",
  calendar: "/app/calendar",
  meetings: "/app/meetings",
  projects: "/app/projects",
  training: "/app/training",
  procedures: "/app/procedures",
  clock: "/app/clock",
  availability: "/app/availability",
  requests: "/app/requests",
  notifications: "/app/notifications",
  feedback: "/app/feedback",
  reminders: "/app/reminders",
  settings: "/app/profile",
};

/**
 * Normalise a navigation target for the native app. Returns a safe relative
 * path, or null if the input is not a same-origin relative path.
 */
export function toNativePath(target: string): string | null {
  if (!target) return null;

  // Reject anything that could navigate off-origin: absolute URLs, protocol
  // relative (//evil.com), and control characters.
  if (/^[a-z][a-z0-9+.-]*:/i.test(target)) return null;
  if (target.startsWith("//")) return null;
  if (!target.startsWith("/")) return null;

  const [rawPath, ...rest] = target.split(/([?#])/);
  const suffix = rest.join("");

  const staffMatch = rawPath.match(/^\/staff(?:\/(.*))?$/);
  if (staffMatch) {
    const segment = (staffMatch[1] || "").replace(/\/+$/, "");
    // Direct mapping first, then the first path segment (e.g. /staff/tasks/123).
    const mapped =
      STAFF_TO_APP[segment] ?? STAFF_TO_APP[segment.split("/")[0]] ?? "/app";
    return mapped + suffix;
  }

  return rawPath + suffix;
}
